/**
 * The Pearson PI login every app runs: the BV and Express dev shells (App.tsx), the EPUB
 * reader, My Library and the comparator viewer. Plain JS with no imports, like
 * appHeaderLinks.js: the shells and the EPUB reader import it, report.js inlines it (minus
 * `export`), and My Library import()s it - from packages/ on the :3030 server, from the copy
 * `yarn uat` puts next to the page.
 *
 * Every app requires a login except on a localhost name (localhost, 127.0.0.1, [::1]): open
 * an app there to skip it. Any other host (workspace-bronte-local.pearson.com:3030, the hosted
 * gateways) signs in through session.js for the tier the hostname names, so the apps on one
 * host share one PI session - the IES SSO cookies on login-*.pearson.com / .pearson.com. An
 * app opened after another one has logged in gets its token without a sign-in form.
 *
 * The token is traded for CloudFront signed cookies at the express gateway's cookie service:
 * they are what lets the browser read the course data (packages, EPUB exports, comparisons)
 * from the bucket.
 */

/** Hosted express gateway, dev tier (`<prefix>.bronte-<tier>.<domain>`, prod `<prefix>.bronte.pearsonprd.tech`). */
export var DEVSHELL_EXPRESS_GATEWAY_DEFAULT =
  "https://bronte-devshell-express.bronte-dev.pearsondev.tech";

/**
 * Local Vite dev-shell host (`vite.config` `server.host`). Port (e.g. :3030) is not part of hostname.
 * Uses the same PI session / viewer env as hosted `bronte-dev` express (`dev` tier → STG PI).
 */
export var LOCAL_DEV_SHELL_VIEWER_HOSTNAME = "bronte-viewer.pearson.com";

/**
 * Local static host (`tts-proxy-server` on :3030) that serves every app (both shells, the EPUB
 * reader, My Library, the comparator). One origin and one `pearson.com`-scoped PI session slot
 * for all of them, so they have to resolve the same tier - otherwise logging into one (BV
 * renderer default QA) invalidates the other (Express default STG).
 */
export var LOCAL_STATIC_SERVER_HOSTNAME = "workspace-bronte-local.pearson.com";

/** session.js per tier (product URLs; not launch origins). */
var SESSION_JS_URLS = {
  dev: "https://login-stg.pearson.com/v1/piapi-int/login/js/v2/session.js",
  stg: "https://login-stg.pearson.com/v1/piapi-int/login/js/v2/session.js",
  qaint: "https://login-qa.pearson.com/v1/piapi-test/login/js/v2/session.js",
  ppe: "https://login-ppe.pearson.com/v1/piapi-pqa/login/js/v2/session.js",
  prod: "https://login.pearson.com/v1/piapi/login/js/v2/session.js",
};

export var PI_CLIENT_ID_PROD = "hQ8ZsSZoIjyl8chQLgo7dB7fAlGIFvVG";
export var PI_CLIENT_ID_NON_PROD = "oX5Vm6SEEeSi5QBEAU0081tx20UHE469";

var COOKIE_SERVICE_PATH = "/cloudfront-signed-cookies-service";

/** Prod shell domains: `<prefix>.<domain>` (pearsonprd.tech is the deployed one, pearson.com an alias). */
var PROD_DOMAINS = ["bronte.pearsonprd.tech", "bronte.pearson.com"];

/**
 * @typedef {"dev" | "qaint" | "stg" | "ppe" | "prod"} PiTier
 */

/** @param {string} s */
function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * @param {string} hostname
 * @returns {boolean}
 */
export function isLocalhostName(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}

/**
 * Hosted express launch tier from `hostname`, using the prefix and non-prod domain taken from
 * {@link DEVSHELL_EXPRESS_GATEWAY_DEFAULT}. Prod shell: `{prefix}.` + one of {@link PROD_DOMAINS}.
 * @param {string} hostname
 * @returns {PiTier | null}
 */
export function parseHostedTier(hostname) {
  if (!hostname) {
    return null;
  }
  var parts = new URL(DEVSHELL_EXPRESS_GATEWAY_DEFAULT).hostname.split(".");
  var prefix = parts[0];
  var domainRest = parts.slice(2).join(".");
  for (var i = 0; i < PROD_DOMAINS.length; i++) {
    if (hostname === prefix + "." + PROD_DOMAINS[i]) {
      return "prod";
    }
  }
  var match = hostname.match(
    new RegExp(
      "^" + escapeRegExp(prefix) + "\\.bronte-([^\\.]+)\\." + escapeRegExp(domainRest) + "$",
    ),
  );
  var tier = match && match[1];
  return tier === "dev" || tier === "qaint" || tier === "stg" || tier === "ppe" ? tier : null;
}

/**
 * The PI tier a hostname names: the hosted gateways plus the local dev-shell hosts (dev).
 * @param {string} hostname
 * @returns {PiTier | null}
 */
export function resolvePiTier(hostname) {
  if (hostname === LOCAL_DEV_SHELL_VIEWER_HOSTNAME || hostname === LOCAL_STATIC_SERVER_HOSTNAME) {
    return "dev";
  }
  return parseHostedTier(hostname);
}

/**
 * @param {PiTier} tier
 * @returns {string}
 */
export function sessionJsUrlForTier(tier) {
  return SESSION_JS_URLS[tier];
}

/**
 * @param {PiTier} tier
 * @returns {string}
 */
export function clientIdForTier(tier) {
  return tier === "prod" ? PI_CLIENT_ID_PROD : PI_CLIENT_ID_NON_PROD;
}

/**
 * The express gateway whose cookie service signs the bucket cookies: the page's own origin on a
 * hosted gateway, the dev gateway everywhere else.
 * @param {Location | URL} location
 * @returns {string}
 */
export function gatewayBaseUrl(location) {
  return parseHostedTier(location.hostname) !== null
    ? location.origin
    : DEVSHELL_EXPRESS_GATEWAY_DEFAULT;
}

/**
 * Prime the CloudFront signed cookies on the express gateway (cookie auth for the bucket).
 * @param {string} gatewayBase
 * @param {string} pearsonToken
 * @returns {Promise<Response>}
 */
export function fetchCloudfrontSignedCookies(gatewayBase, pearsonToken) {
  return fetch(gatewayBase.replace(/\/$/, "") + COOKIE_SERVICE_PATH, {
    method: "GET",
    credentials: "include",
    headers: { "X-Authorization": pearsonToken },
  });
}

/*
 * Guard against the IES sign-in redirect loop.
 *
 * With a stale SSO cookie, login-stg's `oidcauthorize` keeps issuing an `iesCode`
 * (it ignores `prompt=login` while any SSO cookie exists) while `oidctoken` rejects
 * that code with a 400. PI session.js reacts to every failed redeem by redirecting
 * to `prompt=login` again, with no retry bound, so the page reloads forever.
 *
 * The guard counts page loads that arrive carrying an `iesCode` (per tab, in
 * sessionStorage). Once a few arrive in quick succession it hides the code from
 * session.js, initializes without `requireLogin`, and resets the IES session once
 * so the browser lands on a real sign-in form instead of cycling.
 */

var IES_PARAMS = ["iesCode", "iesError", "iesErrorDescription"];
var ARRIVALS_KEY = "bv-ies-code-arrivals";
var RESET_KEY = "bv-ies-session-reset";

export var IES_LOOP_MAX_ARRIVALS = 3;
export var IES_LOOP_WINDOW_MS = 60000;

/**
 * Marker left in the URL for the one initialize() that breaks the loop. Without a
 * token, session.js fires a `prompt=none` redirect on initialize unless the URL
 * carries an `ies*` param (or it did so under five minutes ago, a timestamp the
 * `prompt=login` leg clears). session.js strips `iesError*` from the URL itself.
 */
export var IES_LOOP_GUARD_MARKER = "bv_loop_guard";

export var IES_LOOP_RESET_MESSAGE =
  "IES sign-in loop detected: login-stg kept issuing sign-in codes it then rejected. " +
  "Resetting the IES session…";

export var IES_LOOP_MANUAL_MESSAGE =
  "IES sign-in loop persists after a session reset. Clear cookies for " +
  "login-stg.pearson.com and .pearson.com, then reload.";

/** @returns {number[]} */
function readArrivals() {
  try {
    var parsed = JSON.parse(sessionStorage.getItem(ARRIVALS_KEY) || "[]");
    return Array.isArray(parsed)
      ? parsed.filter(function (t) {
          return typeof t === "number";
        })
      : [];
  } catch (e) {
    return [];
  }
}

/**
 * The URL to hand session.js as `loginSuccessUrl`: the current href minus the IES
 * protocol params. session.js only strips `iesError*` from the redirect target, so
 * a raw href captured on the return leg bakes the `iesCode` into every later
 * redirect and the URL grows by one `&iesCode=` per bounce.
 * @param {string} href
 * @returns {string}
 */
export function stripIesParams(href) {
  var url = new URL(href);
  IES_PARAMS.forEach(function (name) {
    url.searchParams.delete(name);
  });
  return url.toString();
}

/**
 * @param {string} href
 * @returns {string}
 */
export function withIesLoopGuardMarker(href) {
  var url = new URL(href);
  url.searchParams.set("iesError", IES_LOOP_GUARD_MARKER);
  return url.toString();
}

/**
 * Records this page load if it carries an `iesCode`. Returns true once the tab has
 * arrived with a code IES_LOOP_MAX_ARRIVALS times inside IES_LOOP_WINDOW_MS.
 * @returns {boolean}
 */
export function detectIesLoop() {
  try {
    if (!new URL(window.location.href).searchParams.has("iesCode")) {
      return false;
    }
    var now = Date.now();
    var arrivals = readArrivals()
      .filter(function (t) {
        return now - t < IES_LOOP_WINDOW_MS;
      })
      .concat(now);
    sessionStorage.setItem(ARRIVALS_KEY, JSON.stringify(arrivals));
    return arrivals.length >= IES_LOOP_MAX_ARRIVALS;
  } catch (e) {
    return false;
  }
}

/**
 * Claims the single IES session reset this tab may perform. Returns true the first
 * time a loop is broken; false when a reset was already tried, so the caller shows
 * manual instructions instead of resetting again. Arrivals are cleared either way
 * so the sign-in that follows a reset is not miscounted as more of the loop.
 * @returns {boolean}
 */
export function markIesSessionReset() {
  try {
    sessionStorage.removeItem(ARRIVALS_KEY);
    if (sessionStorage.getItem(RESET_KEY)) {
      return false;
    }
    sessionStorage.setItem(RESET_KEY, String(Date.now()));
    return true;
  } catch (e) {
    return false;
  }
}

/** Called once a token was obtained: a later genuine loop may be healed again. */
export function clearIesLoopState() {
  try {
    sessionStorage.removeItem(ARRIVALS_KEY);
    sessionStorage.removeItem(RESET_KEY);
  } catch (e) {
    // storage unavailable: nothing to clear
  }
}

/**
 * Loads session.js into the page's `#sessionJSScript` (added to <head> when the page has none).
 * @param {string} url
 * @returns {Promise<any>} window.piSession
 */
export function loadSessionJs(url) {
  return new Promise(function (resolve, reject) {
    var script = document.getElementById("sessionJSScript");
    if (!script) {
      script = document.createElement("script");
      script.id = "sessionJSScript";
      document.head.appendChild(script);
    }
    script.onload = function () {
      resolve(window.piSession);
    };
    script.onerror = function () {
      reject(new Error("could not load session.js (" + url + ")"));
    };
    script.setAttribute("src", url);
  });
}

/**
 * @typedef {{
 *   sessionJsUrl?: string,
 *   clientId?: string,
 *   cookieGateway?: string | null,
 *   onStatus?: (message: string) => void,
 * }} PiLoginOptions
 *   `sessionJsUrl` / `clientId` default to the hostname's tier (dev when the hostname names
 *   none); `cookieGateway` (default {@link gatewayBaseUrl}) is where the token buys the bucket
 *   cookies, null to skip that; `onStatus` gets "Authorizing..." and the IES loop messages.
 */

/**
 * Requires a PI login: loads session.js, initializes it with `requireLogin` (session.js
 * redirects to the sign-in form when there is no session and returns here afterwards), waits
 * for the token and primes the bucket cookies. A failed cookie request is only logged - the
 * page may not need the bucket.
 *
 * While an IES sign-in loop is being broken the promise never settles (the page is redirected),
 * or it rejects with IES_LOOP_MANUAL_MESSAGE once a reset was already tried in this tab.
 * @param {PiLoginOptions} [options]
 * @returns {Promise<{ token: string, userId: string, piSession: any }>}
 */
export function requirePiLogin(options) {
  var opts = options || {};
  var tier = resolvePiTier(window.location.hostname) || "dev";
  /** @param {string} message */
  var onStatus = function (message) {
    if (opts.onStatus) opts.onStatus(message);
  };
  var cookieGateway =
    opts.cookieGateway === undefined ? gatewayBaseUrl(window.location) : opts.cookieGateway;

  // Never hand session.js a redirect target that still carries an iesCode; otherwise every
  // bounce appends another one.
  var loginSuccessUrl = stripIesParams(window.location.href);
  // Stale IES SSO cookie + rejected codes = unbounded prompt=login redirects. Break the cycle:
  // hide the code from session.js so it cannot redeem-fail-redirect again, and mark the URL
  // so initialize() skips its prompt=none leg.
  var iesLoop = detectIesLoop();
  if (iesLoop) {
    console.warn(
      "PI login: IES sign-in loop detected (authorize keeps issuing codes that oidctoken rejects); breaking it.",
    );
    window.history.replaceState(window.history.state, "", withIesLoopGuardMarker(loginSuccessUrl));
  }

  return loadSessionJs(opts.sessionJsUrl || sessionJsUrlForTier(tier)).then(function (piSession) {
    onStatus("Authorizing...");
    return new Promise(function (resolve, reject) {
      piSession.initialize(opts.clientId || clientIdForTier(tier), {
        loginSuccessUrl: loginSuccessUrl,
        requireLogin: !iesLoop,
        avoidReInit: true,
      });
      piSession.getToken(function (_status, token) {
        if (iesLoop) {
          // The hidden IES frame is loaded by now, so logout() reaches it. It expires the stale
          // SSO cookies and returns to the clean URL, which then lands on a real sign-in form.
          // Only one reset per tab.
          if (markIesSessionReset()) {
            onStatus(IES_LOOP_RESET_MESSAGE);
            piSession.logout(loginSuccessUrl);
          } else {
            onStatus(IES_LOOP_MANUAL_MESSAGE);
            reject(new Error(IES_LOOP_MANUAL_MESSAGE));
          }
          return;
        }
        clearIesLoopState();
        var done = function () {
          resolve({ token: token, userId: piSession.userId(), piSession: piSession });
        };
        if (!cookieGateway) {
          done();
          return;
        }
        fetchCloudfrontSignedCookies(cookieGateway, token)
          .then(function (response) {
            if (!response.ok) {
              console.warn("PI login: CloudFront signed cookies request failed:", response.status);
            }
          })
          .catch(function (error) {
            console.warn("PI login: CloudFront signed cookies request error", error);
          })
          .then(done);
      });
    });
  });
}
